
import { useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ChatSettingsPanel } from './ChatSettingsPanel';
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Search, X, Bot, History, Settings, Check, Zap, Globe, Brain } from "lucide-react";
import { AgentListItem } from "@/types/agent";
import { useChatAgent } from "@/components/chat/ChatAgentContext";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";

interface ChatSidebarProps {
  open: boolean;
  onToggle: () => void;
  selectedAgent: AgentListItem | null;
  selectedAgentId?: string;
  onAgentChange: (agentId: string, agent: AgentListItem) => void;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  isMobile: boolean;
}

export function ChatSidebar({ 
  open, 
  onToggle, 
  selectedAgent,
  selectedAgentId,
  onAgentChange,
  searchQuery, 
  onSearchChange,
  isMobile 
}: ChatSidebarProps) {
  const [activeTab, setActiveTab] = useState<'agents' | 'history' | 'settings'>('agents');
  const { agents, isLoadingAgents } = useChatAgent();

  const filteredAgents = useMemo(() => {
    if (!searchQuery) return agents;
    const query = searchQuery.toLowerCase();
    return agents.filter(agent => 
      agent.name.toLowerCase().includes(query) ||
      agent.role.toLowerCase().includes(query) ||
      agent.goal.toLowerCase().includes(query)
    );
  }, [agents, searchQuery]);

  const sidebarContent = (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-border/50">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">Chat</h2>
          {isMobile && (
            <Button variant="ghost" size="icon" onClick={onToggle}>
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
        
        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={activeTab === 'agents' ? 'Search agents...' : 'Search conversations...'}
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {/* Tabs */}
      <div className="px-4 py-2 border-b border-border/50">
        <div className="flex gap-1">
          {[
            { id: 'agents', label: 'Agents', icon: Bot },
            { id: 'history', label: 'History', icon: History },
            { id: 'settings', label: 'Settings', icon: Settings },
          ].map((tab) => (
            <Button
              key={tab.id}
              variant={activeTab === tab.id ? "secondary" : "ghost"}
              size="sm"
              onClick={() => setActiveTab(tab.id as any)}
              className="flex-1 gap-2"
            >
              <tab.icon className="h-3 w-3" />
              <span className="text-xs">{tab.label}</span>
            </Button>
          ))}
        </div>
      </div>

      {/* Content */}
      <ScrollArea className="flex-1">
        <div className="p-4">
          {activeTab === 'agents' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-medium text-muted-foreground">Available Agents</h3>
                <Badge variant="secondary" className="text-xs">
                  {filteredAgents.length}
                </Badge>
              </div>

              {isLoadingAgents ? (
                <div className="space-y-3">
                  {[...Array(3)].map((_, i) => (
                    <div key={i} className="p-3 rounded-lg border border-border">
                      <div className="flex items-center gap-3">
                        <Skeleton className="h-8 w-8 rounded-full" />
                        <div className="flex-1 space-y-2">
                          <Skeleton className="h-4 w-24" />
                          <Skeleton className="h-3 w-32" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : filteredAgents.length === 0 ? (
                <div className="text-center py-8">
                  <Bot className="h-12 w-12 mx-auto text-muted-foreground/50 mb-3" />
                  <p className="text-sm text-muted-foreground">
                    {searchQuery ? 'No agents found matching your search' : 'No agents available'}
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {filteredAgents.map((agent) => {
                    // Use both selectedAgent and selectedAgentId for reliable selection detection
                    const isSelected = selectedAgent?.id === agent.id || selectedAgentId === agent.id;
                    return (
                      <button
                        key={agent.id}
                        onClick={() => onAgentChange(agent.id, agent)}
                        className={`w-full p-3 rounded-lg border text-left transition-colors hover:bg-muted/50 ${
                          isSelected 
                            ? 'border-primary bg-primary/5' 
                            : 'border-border bg-background/50'
                        }`}
                      >
                        <div className="relative flex items-start gap-3">
                          {/* Source indicator icon in top right */}
                          {agent.source && (
                            <div className="absolute -top-1 -right-1 p-1 rounded-full bg-background border border-border/50 shadow-sm">
                              {agent.source === 'elevenlabs' ? (
                                <Globe className="h-2.5 w-2.5 text-purple-600" />
                              ) : (
                                <Zap className="h-2.5 w-2.5 text-blue-600" />
                              )}
                            </div>
                          )}
                          
                          <div className={`relative mt-0.5 p-1.5 rounded-full ${
                            isSelected ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
                          }`}>
                            <Bot className="h-3 w-3" />
                            {agent.active && (
                              <div className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-green-500 rounded-full border border-background" />
                            )}
                          </div>
                          
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <p className="text-sm font-medium truncate">{agent.name}</p>
                              {isSelected && <Check className="h-3 w-3 text-primary shrink-0" />}
                            </div>
                            
                            <p className="text-xs text-muted-foreground mb-2">{agent.role}</p>
                            
                            {agent.goal && (
                              <p className="text-xs text-muted-foreground/80 line-clamp-2 mb-2">
                                {agent.goal}
                              </p>
                            )}
                            
                            <div className="flex items-center gap-2 flex-wrap">
                              {agent.llm && (
                                <Badge variant="outline" className="text-xs h-5">
                                  <Brain className="h-2 w-2 mr-1" />
                                  {agent.llm.provider.toUpperCase()} {agent.llm.model}
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {activeTab === 'history' && (
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-muted-foreground">Recent Conversations</h3>
              <p className="text-sm text-muted-foreground">No conversation history yet.</p>
            </div>
          )}

          {activeTab === 'settings' && (
            <div className="px-2">
              <ChatSettingsPanel />
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  );

  if (isMobile) {
    return (
      <Sheet open={open} onOpenChange={onToggle}>
        <SheetContent side="left" className="w-80 p-0">
          <SheetHeader className="sr-only">
            <SheetTitle>Chat Sidebar</SheetTitle>
          </SheetHeader>
          {sidebarContent}
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <div
      className={`shrink-0 border-r border-border/50 bg-background/50 backdrop-blur-sm transition-all duration-300 ${
        open ? 'w-80' : 'w-0 overflow-hidden'
      }`}
    >
      {sidebarContent}
    </div>
  );
}
